/**
 * API Utility
 * 
 * Provides configured API clients for making authenticated requests to backend services.
 * Automatically injects Okta authentication tokens into requests.
 */

import axios from 'axios';

/**
 * AWS Configuration
 * 
 * Production Domain: https://autoapproval.prajnagpt.net
 * All endpoints use environment variables with fallback defaults
 */
export const AWS_CONFIG = {
  region: 'us-east-2',
  identityPoolId: 'us-east-2:98c5c43a-40bd-4894-85be-e2a0f1243161',
  apis: {
    insurance: {
      name: 'InsuranceAutoApprovalAPI',
      endpoint: process.env.REACT_APP_INSURANCE_API_ENDPOINT || 
                'https://6nsqdy352j.execute-api.us-east-2.amazonaws.com/prod',
      region: 'us-east-2'
    },
    title: {
      name: 'TitleAutoApprovalAPI',
      endpoint: process.env.REACT_APP_TITLE_API_ENDPOINT ||
                'https://4j0ecqpn44.execute-api.us-east-2.amazonaws.com/prod',
      region: 'us-east-2'
    }
  },
  storage: {
    bucket: process.env.REACT_APP_S3_BUCKET || 'doc-extraction-in-prajna-dev',
    region: 'us-east-2'
  }
};

/**
 * Create an axios instance with default configuration
 */
const createApiClient = (baseURL) => {
  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  return client;
};

/**
 * Insurance API Client
 */
export const insuranceApi = createApiClient(AWS_CONFIG.apis.insurance.endpoint);

/**
 * Title API Client
 */
export const titleApi = createApiClient(AWS_CONFIG.apis.title.endpoint);

/**
 * Set authentication token for API requests
 * Call this method to inject the Okta token into all subsequent requests
 * 
 * @param {string} token - Okta access token or ID token
 */
export const setAuthToken = (token) => {
  if (token) {
    insuranceApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    titleApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete insuranceApi.defaults.headers.common['Authorization'];
    delete titleApi.defaults.headers.common['Authorization'];
  }
};

/**
 * Configure API clients with Okta authentication
 * This should be called after user logs in with Okta
 * 
 * @param {Function} getTokenFn - Function that returns a Promise resolving to the access token
 */
export const configureApiAuth = (getTokenFn) => {
  // Add request interceptor to inject fresh token before each request
  const requestInterceptor = async (config) => {
    try {
      const token = await getTokenFn();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
    return config;
  };

  const errorInterceptor = (error) => {
    return Promise.reject(error);
  };

  // Add interceptors to both API clients
  insuranceApi.interceptors.request.use(requestInterceptor, errorInterceptor);
  titleApi.interceptors.request.use(requestInterceptor, errorInterceptor);

  // Add response interceptor to handle auth errors
  const responseErrorInterceptor = (error) => {
    if (error.response?.status === 401) {
      console.error('Authentication error - token may be expired');
      // Optionally trigger logout or token refresh
      window.location.href = '/login';
    }
    return Promise.reject(error);
  };

  insuranceApi.interceptors.response.use(
    (response) => response,
    responseErrorInterceptor
  );
  
  titleApi.interceptors.response.use(
    (response) => response,
    responseErrorInterceptor
  );
};

/**
 * API Helper Functions
 */

// Insurance API Endpoints
export const insuranceApiMethods = {
  /**
   * Get all documents with optional filters
   * @param {Object} params - Query parameters (company, status, limit)
   */
  getDocuments: (params = {}) => {
    return insuranceApi.get('/documents', { params });
  },

  /**
   * Get a specific document by ID
   * @param {string} docId - Document ID
   */
  getDocument: (docId) => {
    return insuranceApi.get(`/documents/${docId}`);
  },

  /**
   * Upload document metadata (after S3 upload)
   * @param {Object} data - Document metadata
   */
  createDocument: (data) => {
    return insuranceApi.post('/documents', data);
  },

  deleteDocument: (docId) => {
    return insuranceApi.delete(`/documents/${docId}`);
  }
};

// Title API Endpoints
export const titleApiMethods = {
  /**
   * Get all title documents with optional filters
   * @param {Object} params - Query parameters
   */
  getDocuments: (params = {}) => {
    return titleApi.get('/documents', { params });
  },

  /**
   * Get a specific title document by ID
   * @param {string} docId - Document ID
   */
  getDocument: (docId) => {
    return titleApi.get(`/documents/${docId}`);
  },

  /**
   * Upload title document metadata
   * @param {Object} data - Document metadata
   */
  createDocument: (data) => {
    return titleApi.post('/documents', data);
  },

  deleteDocument: (docId) => {
    return titleApi.delete(`/documents/${docId}`);
  }
};

/**
 * S3 Upload Helper
 * This provides a wrapper for S3 uploads using signed URLs or direct upload
 * 
 * Note: With Okta auth, you have several options:
 * 1. Get temporary AWS credentials by exchanging Okta token (recommended)
 * 2. Use presigned URLs from your backend
 * 3. Upload through your backend as a proxy
 */
export const uploadToS3 = async (file, oktaToken, options = {}) => {
  const {
    bucket = AWS_CONFIG.storage.bucket,
    key,
    onProgress
  } = options;

  try {
    // Option 1: Get presigned URL from backend
    const presignedUrlResponse = await insuranceApi.post('/upload/presigned-url', {
      filename: key || file.name,
      contentType: file.type,
      bucket
    }, {
      headers: {
        Authorization: `Bearer ${oktaToken}`
      }
    });

    const { uploadUrl, fileKey } = presignedUrlResponse.data;

    // Upload file directly to S3 using presigned URL
    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': file.type
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      }
    });

    return { fileKey, bucket };
  } catch (error) {
    console.error('S3 upload error:', error);
    throw error;
  }
};

export default {
  insuranceApi,
  titleApi,
  setAuthToken,
  configureApiAuth,
  insuranceApiMethods,
  titleApiMethods,
  uploadToS3,
  AWS_CONFIG
};
